import type { RequestTourValues, RequestScheduleTourMessage } from './types'
import type { PrescreenFields } from 'containers/public-listing/contact-drawer/pages/prescreen-form'

import { useMediaQuery, useTheme } from '@mui/material'
import CircularProgress from '@rentspree/component-2023.components.organisms.circular-progress'
import Drawer from '@rentspree/component-2023.components.organisms.drawer'
import { useFormik } from 'formik'
import { useEffect, useState, useCallback } from 'react'
import { useTurnstile } from 'react-turnstile'

import { PrescreenForm } from 'containers/public-listing/contact-drawer/pages/prescreen-form'
import { parsePrescreenFormData } from 'containers/public-listing/contact-drawer/utils'
import { prescreenFormValidation } from 'containers/public-listing/contact-drawer/validations'
import env from 'env'
import { useDispatch, useSelector } from 'store'
import { selectAgentProfile } from 'store/agent-profile'
import { showAlertDialog } from 'store/alert-dialog'
import { selectPublicListingDataToRCM } from 'store/public-listing'
import { selectScheduleTourApiStatus } from 'store/public-messaging'
import { asyncActions } from 'store/public-messaging/async-actions'
import { selectUserProfile } from 'store/users'
import tracker from 'tracker'
import { LOCATION, MAIN_FEATURE } from 'tracker/events'

import { generateTourDefaultMessage, MESSAGE_TYPE } from './constants'
import { ScheduleTourForm } from './request-tour-form'
import { requestTourFormValidation } from './validations'

interface RequestTourProps {
  isOpen: boolean
  onClose: () => void
  contactDetails: ContactDetails
}

export const RequestTourDrawer: React.FC<RequestTourProps> = ({
  isOpen,
  onClose,
  contactDetails,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const dispatch = useDispatch()

  const { userId, email: agentEmail } = useSelector(selectAgentProfile)
  const publicListingDataToRCM = useSelector(selectPublicListingDataToRCM)
  const userProfile = useSelector(selectUserProfile) || {}
  const tourApiResponseStatus = useSelector(selectScheduleTourApiStatus)

  const [tourApiRequestSent, setTourApiRequestSent] = useState(false)
  const [isPrescreenForm, setIsPrescreenForm] = useState(false)
  const isTurnstileEnabled = env?.TURNSTILE_ENABLED
  const turnstile = useTurnstile()
  const [isTurnstileLoading, setIsTurnstileLoading] = useState(false)

  const defaultMessage = generateTourDefaultMessage(
    userProfile,
    contactDetails?.displayName ?? '',
    publicListingDataToRCM.address,
  )

  const isSubmittingRequestTour = tourApiResponseStatus === 'pending'

  const formik = useFormik<RequestTourValues>({
    initialValues: {
      selectedDates: [],
      message: defaultMessage,
      moveInDate: '',
      preferredLeaseTerm: '',
      householdMonthlyIncome: '',
      occupy: '1',
    },
    onSubmit: (values) => {
      const prescreenFields: PrescreenFields = {
        moveInDate: values.moveInDate,
        preferredLeaseTerm: values.preferredLeaseTerm,
        householdMonthlyIncome: values.householdMonthlyIncome,
        occupy: values.occupy,
      }
      const prescreenMessageData = parsePrescreenFormData(prescreenFields)
      const { selectedDates = [], message } = values

      const messages: RequestScheduleTourMessage[] = [
        { data: message || defaultMessage, type: MESSAGE_TYPE.MESSAGE },
      ]

      messages.push({
        data: { ...prescreenMessageData, propertyId: publicListingDataToRCM.id },
        type: MESSAGE_TYPE.PRE_SCREEN,
      })

      messages.push({
        data: {
          preferredDate: selectedDates,
          propertyAddress: publicListingDataToRCM.address,
        },
        type: MESSAGE_TYPE.SCHEDULE_TOUR,
      })

      const turnstileToken = isTurnstileEnabled ? turnstile.getResponse() : ''

      if (userId && agentEmail) {
        const payload = {
          sender: {
            fullName: userProfile?.fullName || '',
          },
          recipient: {
            _id: userId || '',
            email: agentEmail || '',
          },
          messages,
          property: publicListingDataToRCM,
        }
        void dispatch(
          asyncActions.sendScheduleTourRequestToAgent({
            ...payload,
            source: 'listing-syndication',
            turnstile,
            turnstileToken,
          }),
        )
        setTourApiRequestSent(true)
      }
    },
    validationSchema: isPrescreenForm ? prescreenFormValidation : requestTourFormValidation,
  })

  const handleSubmit = async (): Promise<void> => {
    const errors = await formik.validateForm()
    const hasErrors = Object.keys(errors).length > 0

    if (hasErrors) {
      const touchedFields = Object.keys(errors).reduce<Record<string, boolean>>(
        (acc, key) => ({
          ...acc,
          [key]: true,
        }),
        {},
      )
      void formik.setTouched(touchedFields)
      return
    }

    if (isPrescreenForm) {
      tracker?.trackButtonClick({
        click_text: 'Request tour',
        location: LOCATION.REQUEST_TOUR_MODAL,
        screen_experience: isMobile ? 'mobile' : 'desktop',
        main_feature: MAIN_FEATURE.MARKET_LISTING,
      })
      formik.handleSubmit()
    } else {
      tracker?.trackButtonClick({
        click_text: 'Continue',
        location: LOCATION.REQUEST_TOUR_MODAL,
        screen_experience: isMobile ? 'mobile' : 'desktop',
        main_feature: MAIN_FEATURE.MARKET_LISTING,
      })
      setIsPrescreenForm(true)
    }
  }

  useEffect(() => {
    if (isOpen) {
      formik.resetForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const onCloseRequestTour = useCallback(() => {
    setIsPrescreenForm(false)
    onClose()
  }, [onClose])

  useEffect(() => {
    if (tourApiRequestSent) {
      switch (tourApiResponseStatus) {
        case 'fulfilled':
          onCloseRequestTour()
          dispatch(
            showAlertDialog({
              title: 'Tour Requested',
              message: 'The listing agent should respond to your request soon.',
              type: 'success',
            }),
          )
          break
        case 'rejected':
          onCloseRequestTour()
          dispatch(
            showAlertDialog({
              title: 'Request Failed',
              message: 'Something went wrong while requesting a tour. Please try again.',
              type: 'error',
              ctaTitle: 'OK',
            }),
          )
          break
        default:
          break
      }
    }
  }, [dispatch, onCloseRequestTour, tourApiRequestSent, tourApiResponseStatus])

  return (
    <Drawer
      data-testid="request-tour-drawer"
      className="visibility-tracking element_visibility"
      data-event="element_visibility"
      data-location={LOCATION.REQUEST_TOUR_MODAL}
      data-main_feature={MAIN_FEATURE.MARKET_LISTING}
      data-screen_experience={isMobile ? 'mobile' : 'desktop'}
      stickyHeader={false}
      anchor="right"
      size="small"
      open={isOpen}
      onClose={() => {
        tracker?.trackButtonClick({
          click_text: 'Close',
          location: LOCATION.REQUEST_TOUR_MODAL,
          screen_experience: isMobile ? 'mobile' : 'desktop',
          main_feature: MAIN_FEATURE.MARKET_LISTING,
        })
        onCloseRequestTour()
      }}
      displayCloseButton
      title={isPrescreenForm ? 'Add a few more details' : 'Request a tour'}
      actionButtonsProps={{
        primaryButtonText: isPrescreenForm ? 'Request tour' : 'Continue',
        primaryButtonProps: {
          onClick: handleSubmit,
          disabled: isSubmittingRequestTour || isTurnstileLoading,
          startIcon: isSubmittingRequestTour && (
            <CircularProgress sx={{ color: 'grey !important' }} size="icon" />
          ),
          color: isPrescreenForm ? 'primary' : 'secondary',
        },
        ...(isPrescreenForm && {
          secondaryButtonText: 'Back',
          secondaryButtonProps: {
            variant: 'text',
            wrapped: true,
            onClick: () => {
              tracker?.trackButtonClick({
                click_text: 'Back',
                location: LOCATION.REQUEST_TOUR_MODAL,
                screen_experience: isMobile ? 'mobile' : 'desktop',
                main_feature: MAIN_FEATURE.MARKET_LISTING,
              })
              setIsPrescreenForm(false)
            },
          },
        }),
      }}
    >
      {isPrescreenForm ? (
        <PrescreenForm formik={formik} setIsTurnstileLoading={setIsTurnstileLoading} />
      ) : (
        <ScheduleTourForm formik={formik} />
      )}
    </Drawer>
  )
}
